import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import { apiClient } from "../lib/api";
import { imageId } from "../lib/ids";
import { ContactForm } from "./components/ContactForm";

const ArtworkDetailPage = () => {
  const { id = "" } = useParams<{ id: string }>();
  const query = useQuery({
    queryKey: ["artwork", id],
    queryFn: () => apiClient.frontend.GetArtwork(id),
    enabled: id !== "",
  });
  const [active, setActive] = useState(0);

  if (query.isLoading) {
    return (
      <p className="text-center text-sm text-[var(--color-kj-text-light)] py-24">
        Loading…
      </p>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div className="text-center py-24">
        <p className="font-display text-2xl mb-3">Not found</p>
        <p className="text-sm text-[var(--color-kj-text-light)] mb-6">
          This piece may have moved.
        </p>
        <Link
          to="/"
          className="font-display uppercase-track text-xs border-b border-[var(--color-kj-border)] hover:border-[var(--color-kj-text)] transition-colors"
        >
          ← Back to all work
        </Link>
      </div>
    );
  }

  const a = query.data.Artwork;
  const cover = a.Images[active] ?? a.Images[0];

  return (
    <section className="max-w-6xl mx-auto px-6 md:px-8 py-12">
      <Link
        to="/"
        className="inline-block mb-10 text-xs uppercase-track text-[var(--color-kj-text-light)] hover:text-[var(--color-kj-text)] transition-colors"
      >
        ← All work
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
        <div>
          <div className="relative aspect-[4/5] bg-[var(--color-kj-placeholder)] border border-[var(--color-kj-border)] overflow-hidden">
            {cover ? (
              <img
                src={cover.URL}
                alt={a.Title}
                className="w-full h-full object-cover transition-all"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center font-display italic text-[var(--color-kj-accent)]">
                Image coming soon
              </div>
            )}
          </div>
          {a.Images.length > 1 && (
            <div className="mt-4 grid grid-cols-5 gap-2">
              {a.Images.map((img, i) => (
                <button
                  type="button"
                  key={imageId(img.ID)}
                  onClick={() => setActive(i)}
                  className={[
                    "aspect-square overflow-hidden border transition-colors cursor-pointer",
                    i === active
                      ? "border-[var(--color-kj-text)]"
                      : "border-[var(--color-kj-border)]",
                  ].join(" ")}
                >
                  <img
                    src={img.URL}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <h1 className="font-display text-4xl uppercase-track mb-6">{a.Title}</h1>

          {a.Description && (
            <p className="text-sm leading-loose whitespace-pre-wrap text-[var(--color-kj-text-light)] mb-10">
              {a.Description}
            </p>
          )}

          <div className="border-t border-[var(--color-kj-border)] pt-10">
            <p className="font-display text-xl uppercase-track text-center mb-2">
              Inquire about this piece
            </p>
            <p className="text-xs uppercase-track text-[var(--color-kj-text-light)] text-center mb-8">
              {a.Title}
            </p>
            <ContactForm
              artworkId={id}
              headerText=""
              initialMessage={`Hi Kinsey,\n\nI'd love to hear more about "${a.Title}".\n\nThanks!`}
              submitLabel="Send Inquiry"
              successText="Message sent. Kinsey will be in touch."
              messageRows={9}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ArtworkDetailPage;
